/**
 * Merkezi TanStack Query key fabrikası. CMS sayfaları + invalidator
 * yardımcıları aynı yerden okuyor.
 *
 * Konvansiyon: ["domain", "sub", ...params] dizisi. Domain root key'lerini
 * `cmsKeys` üzerinden okuyarak surgical invalidate yapıyoruz.
 */

export const cmsKeys = {
  // Account
  me: ["me"] as const,

  // Dashboard
  dashboard: ["dashboard"] as const,
  dashboardSummary: () => ["dashboard", "summary"] as const,

  // Users
  users: ["users"] as const,
  usersList: (params: Record<string, unknown>) => ["users", "list", params] as const,
  user: (id: string) => ["users", "detail", id] as const,
  userUsage: (id: string) => ["users", id, "usage"] as const,
  userCost: (id: string) => ["users", id, "cost"] as const,
  userContents: (id: string) => ["users", id, "contents"] as const,

  // Plans
  plans: ["plans"] as const,
  plan: (id: string) => ["plans", id] as const,

  // Subscriptions
  subscriptions: ["subscriptions"] as const,
  subscription: (id: string) => ["subscriptions", id] as const,

  // Payments
  payments: ["payments"] as const,
  payment: (id: string) => ["payments", id] as const,

  // Cost
  cost: ["cost"] as const,
  costSummary: () => ["cost", "summary"] as const,
  costDaily: (from?: string, to?: string) => ["cost", "daily", { from, to }] as const,
  costUsage: (days: number) => ["cost", "usage", days] as const,
  costByContent: (limit: number) => ["cost", "by-content", limit] as const,
  costTopContents: (limit: number) => ["cost", "top-contents", limit] as const,
  costTopUsers: (limit: number) => ["cost", "top-users", limit] as const,
  costByUser: (limit: number) => ["cost", "by-user", limit] as const,

  // System
  systemSettings: ["system-settings"] as const,
  systemSetting: (key: string) => ["system-settings", key] as const,
  credentials: ["system-settings", "credentials"] as const,

  // API pool
  apiPool: ["api-pool"] as const,
  apiPoolList: (provider: string) => ["api-pool", "list", provider] as const,
  apiPoolAvailability: ["api-pool", "availability"] as const,

  // External services
  externalServices: ["external-services"] as const,
  externalServicesList: (params: Record<string, unknown>) =>
    ["external-services", "list", params] as const,
  externalService: (id: string) => ["external-services", "detail", id] as const,
  externalServiceAudit: (id: string) =>
    ["external-services", "audit", id] as const,

  // AI rules
  aiPatterns: ["ai-patterns"] as const,
  aiPattern: (id: string) => ["ai-patterns", id] as const,
  aiPhrases: ["ai-phrases"] as const,
  aiPhrase: (id: string) => ["ai-phrases", id] as const,
  protectedTerms: ["protected-terms"] as const,

  // Sector
  industries: ["industries"] as const,
  industry: (id: string) => ["industries", id] as const,
  services: ["industry-services"] as const,
  service: (id: string) => ["industry-services", id] as const,
  masterPrompts: ["master-prompts"] as const,
  masterPrompt: (id: string) => ["master-prompts", id] as const,

  // Cross-tenant
  contentTypes: ["content-types"] as const,
  brandKits: ["brand-kits"] as const,

  // Ops
  jobs: ["jobs"] as const,
  recovery: ["recovery"] as const,
  audit: ["audit"] as const,
  health: ["health"] as const,
  healthDetailed: () => ["health", "detailed"] as const,
  healthQueueDepth: () => ["health", "queue-depth"] as const,
  healthComponents: () => ["health", "components"] as const,

  // Analytics
  analytics: ["analytics"] as const,
  analyticsUsers: (days?: number) => ["analytics", "users", { days }] as const,
  analyticsRevenue: () => ["analytics", "revenue"] as const,
  analyticsUsage: (days?: number) => ["analytics", "usage", { days }] as const,
  growthFunnel: (days?: number) => ["growth", "funnel", { days }] as const,
  growthWriter: (params: Record<string, unknown>) => ["growth", "writer", params] as const,
  growthDrip: ["growth", "drip"] as const,
  analyticsTopContents: (limit?: number) => ["analytics", "top-contents", { limit }] as const,
  analyticsTopUsers: (limit?: number) => ["analytics", "top-users", { limit }] as const,
};
